import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Printer, Home, Download, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

export default function RegisterSuccess() {
  const { regNo } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/youth-public/${regNo}`).then(({ data }) => setData(data)).catch(() => {});
  }, [regNo]);

  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen bg-paper">
      <div className="h-1.5 gradient-stripe no-print" />

      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="text-center mb-6 no-print">
          <div className="inline-flex w-16 h-16 rounded-full bg-[#10B981] items-center justify-center mb-4 animate-pulse-ring">
            <CheckCircle2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading font-bold text-3xl">Registration Successful!</h1>
          <p className="text-gray-600 mt-1">Your details have been recorded with padaskillinfo.</p>
        </div>

        {/* Certificate */}
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden" data-testid="certificate">
          <div className="gradient-stripe h-2" />
          <div className="p-8 sm:p-12">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#1E3A8A] flex items-center justify-center text-white font-heading font-bold text-lg">PS</div>
                <div>
                  <div className="font-heading font-bold text-xl">padaskillinfo</div>
                  <div className="text-xs text-gray-500">Pithapuram Skilling Census · RTIH</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-500 uppercase tracking-wider">Registration No.</div>
                <div className="font-heading font-bold text-[#E11D48] text-lg" data-testid="reg-no">{regNo}</div>
              </div>
            </div>

            <div className="text-center py-6 border-y border-dashed border-gray-200 my-6">
              <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Certificate of Registration</div>
              <h2 className="font-heading font-bold text-3xl text-[#1E3A8A]">{data?.full_name || "—"}</h2>
              <p className="text-gray-600 mt-2 max-w-xl mx-auto">
                has been successfully registered for the Pithapuram Constituency Skilling Census
                under the RTIH Incubation Centre's youth empowerment initiative.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <Row k="Village" v={data?.village} />
              <Row k="Aadhaar" v={data?.aadhaar_number} />
              <Row k="Mobile" v={data?.mobile} />
              <Row k="Education" v={`${data?.education_level || "—"} (${data?.education_status || "—"})`} />
              <Row k="Looking for" v={data?.placement_preference} />
              <Row k="AI Upskilling" v={data?.ai_upskill_interest ? "Yes" : "No"} />
              <Row k="Domains" v={data?.interested_domains?.join(", ") || "—"} />
              <Row k="Bank Linkage" v={data?.bank_linkage_required ? "Required" : "Not required"} />
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200 flex items-end justify-between text-xs text-gray-500">
              <div>
                <div>Issued on</div>
                <div className="font-heading font-semibold text-gray-700">{data?.created_at ? new Date(data.created_at).toLocaleDateString() : "—"}</div>
              </div>
              <div className="text-right">
                <div className="font-heading font-bold text-[#1E3A8A]">RTIH Pithapuram</div>
                <div>Authorised Signatory</div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 justify-center mt-6 no-print">
          <Button onClick={handlePrint} className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90" data-testid="btn-print">
            <Printer className="w-4 h-4 mr-2" /> Print / Save PDF
          </Button>
          <Link to="/"><Button variant="outline"><Home className="w-4 h-4 mr-2" /> Home</Button></Link>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3 border-b border-gray-100 py-1.5">
      <span className="text-gray-500">{k}</span>
      <span className="font-medium text-gray-800 text-right">{v || "—"}</span>
    </div>
  );
}
